/**
 * PostTripConfirmation — "Was this your exact destination?" prompt
 * Shown after ride completion. Non-blocking overlay.
 */
import { useState, useRef, useEffect } from 'react';
import { MapPin, Check, Move, X, Save, Home, Briefcase, BookOpen, Church, Tag } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const SAVE_PRESETS = [
  { label: 'Home', icon: Home },
  { label: 'Work', icon: Briefcase },
  { label: 'School', icon: BookOpen },
  { label: 'Church', icon: Church },
];

function DraggablePin({ position, onMove }) {
  const markerRef = useRef(null);
  useEffect(() => {
    const marker = markerRef.current;
    if (marker) {
      marker.on('dragend', () => {
        const { lat, lng } = marker.getLatLng();
        onMove(lat, lng);
      });
    }
  }, [onMove]);
  return <Marker position={position} icon={redIcon} draggable ref={markerRef} />;
}

export default function PostTripConfirmation({ rideId, destination, onDismiss }) {
  const [step, setStep] = useState('ask'); // ask | adjust | save | done
  const [correctedPos, setCorrectedPos] = useState(null);
  const [saveLabel, setSaveLabel] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const lat = destination?.lat || 0;
  const lng = destination?.lng || 0;
  const name = destination?.name || destination?.address || 'Destination';

  const submit = async (confirmation, corrLat = null, corrLng = null, save = null) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/location-intelligence/trip-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ride_id: rideId,
          original_name: name,
          original_lat: lat,
          original_lng: lng,
          confirmation,
          corrected_lat: corrLat,
          corrected_lng: corrLng,
          save_as: save,
        }),
      });
      if (confirmation === 'exact') toast.success('Location confirmed! Thanks for helping improve Jamaica maps.');
      else if (confirmation === 'close') toast.success('Correction saved — thank you!');
      else toast.info('Noted — we\'ll work on improving this location.');
    } catch (e) { /* silent fail — non-critical */ }
    setSubmitting(false);
    // Don't auto-dismiss if we're going to show save options
    if (!save && confirmation !== 'wrong') return;
    setStep('done');
    setTimeout(onDismiss, 1500);
  };

  if (step === 'done') return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-end justify-center bg-black/30" data-testid="post-trip-confirmation">
      <div className="w-full max-w-md bg-white rounded-t-2xl shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h3 className="text-base font-bold text-gray-900">Was this your exact destination?</h3>
          <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <p className="px-5 text-sm text-gray-500 mb-3">{name}</p>

        {/* Step: Ask */}
        {step === 'ask' && (
          <div className="px-5 pb-5 space-y-2">
            <button onClick={() => setStep('save_exact')}
              className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition text-left"
              data-testid="confirm-exact">
              <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Yes, this was correct</p>
                <p className="text-xs text-gray-500">The pin was at the right spot</p>
              </div>
            </button>

            <button onClick={() => { setCorrectedPos([lat, lng]); setStep('adjust'); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 transition text-left"
              data-testid="confirm-close">
              <Move className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Close, let me adjust it</p>
                <p className="text-xs text-gray-500">Move the pin to the exact point</p>
              </div>
            </button>

            <button onClick={() => submit('wrong')}
              className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 transition text-left"
              data-testid="confirm-wrong">
              <X className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">No, this was not correct</p>
                <p className="text-xs text-gray-500">The destination was wrong</p>
              </div>
            </button>
          </div>
        )}

        {/* Step: Adjust pin */}
        {step === 'adjust' && correctedPos && (
          <div className="px-5 pb-5">
            <p className="text-xs text-gray-500 mb-2">Drag the pin to your exact destination</p>
            <div className="h-48 rounded-xl overflow-hidden border border-gray-200 mb-3">
              <MapContainer center={correctedPos} zoom={17} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
                <DraggablePin position={correctedPos} onMove={(newLat, newLng) => setCorrectedPos([newLat, newLng])} />
              </MapContainer>
            </div>
            <button onClick={() => { submit('close', correctedPos[0], correctedPos[1]); setStep('save_close'); }}
              disabled={submitting}
              className="w-full py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition"
              data-testid="submit-correction">
              Confirm adjusted location
            </button>
          </div>
        )}

        {/* Step: Offer to save (after exact or close confirmation) */}
        {(step === 'save_exact' || step === 'save_close') && (
          <div className="px-5 pb-5">
            {step === 'save_exact' && <p className="text-sm text-emerald-600 font-medium mb-2">Location confirmed!</p>}
            {step === 'save_close' && <p className="text-sm text-amber-600 font-medium mb-2">Correction saved!</p>}
            <p className="text-sm text-gray-600 mb-3">Save this place for quick booking?</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {SAVE_PRESETS.map(({ label, icon: Icon }) => (
                <button key={label} onClick={() => {
                  setSaveLabel(label);
                  const conf = step === 'save_exact' ? 'exact' : 'close';
                  const cLat = step === 'save_close' && correctedPos ? correctedPos[0] : null;
                  const cLng = step === 'save_close' && correctedPos ? correctedPos[1] : null;
                  submit(conf, cLat, cLng, label);
                }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-sm hover:bg-gray-100 transition"
                  data-testid={`save-as-${label.toLowerCase()}`}>
                  <Icon className="w-3.5 h-3.5 text-gray-500" /> {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text" placeholder="Custom label..."
                className="flex-1 px-3 py-1.5 border rounded-lg text-sm" maxLength={30}
                value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
                data-testid="custom-label-input"
              />
              {customLabel && (
                <button onClick={() => {
                  const conf = step === 'save_exact' ? 'exact' : 'close';
                  const cLat = step === 'save_close' && correctedPos ? correctedPos[0] : null;
                  const cLng = step === 'save_close' && correctedPos ? correctedPos[1] : null;
                  submit(conf, cLat, cLng, customLabel);
                }}
                  className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm font-medium">
                  <Save className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button onClick={() => {
              const conf = step === 'save_exact' ? 'exact' : 'close';
              const cLat = step === 'save_close' && correctedPos ? correctedPos[0] : null;
              const cLng = step === 'save_close' && correctedPos ? correctedPos[1] : null;
              submit(conf, cLat, cLng);
            }} className="w-full mt-3 text-sm text-gray-400 hover:text-gray-600">
              Skip saving
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
